import { SYSTEMS } from '../constants';
import { RateRequestFormFields } from './UploadAnalyzeForm';
import { COLUMN_NAMES } from './columnNames';
import { WorkbookColumnBuilders } from '../../@types';

/**
 * Column builders for Rating Analysis Configuration tab
 * Must match AnalysisInputConfiguration.cs order exactly
 *
 * Note: Data Type, Rate Type, Request Type are for UX/Form purposes only
 * and should not be sent to RateQuality
 */

type Systems = typeof SYSTEMS[keyof typeof SYSTEMS];

export const analysisRequestColumnBuilders: WorkbookColumnBuilders<RateRequestFormFields> =
  {
    // 1. Pull Data
    [COLUMN_NAMES.PULL_DATA]: () => {
      // Not currently used in the form - defaults to empty
      return '';
    },

    // 2-3. From/To Date
    [COLUMN_NAMES.FROM_DATE]: () => {
      // Not currently used in the form - defaults to empty
      return '';
    },
    [COLUMN_NAMES.TO_DATE]: () => {
      // Not currently used in the form - defaults to empty
      return '';
    },

    // 4. Account Name
    [COLUMN_NAMES.ACCOUNT_NAME]: (formFields) => {
      return formFields.accounts.map((v) => v?.name).join(',');
    },

    // 5. Account Number
    [COLUMN_NAMES.ACCOUNT_NUMBER]: (formFields) => {
      return formFields.accounts.map((v) => v?.accountNumber).join(',');
    },

    // 6. Carrier Tiers
    [COLUMN_NAMES.CARRIER_TIERS]: (formFields) => {
      return formFields.carrierTiers.join(',');
    },

    // 7. Carrier Exclusion
    [COLUMN_NAMES.CARRIER_EXCLUSION]: (formFields) => {
      return formFields.carrierExclusions
        ?.map((carrier) => carrier.value)
        .join(',') || '';
    },

    // 8. Carrier Inclusion
    [COLUMN_NAMES.CARRIER_INCLUSION]: (formFields) => {
      return formFields.carrierInclusions
        ?.map((carrier) => carrier.value)
        .join(',') || '';
    },

    // 9. Analysis Type
    [COLUMN_NAMES.ANALYSIS_TYPE]: (formFields) => {
      return formFields.analysisType || '';
    },

    // 10. Markup Type
    [COLUMN_NAMES.MARKUP_TYPE]: (formFields) => {
      return formFields.markupType || '';
    },

    // 11. Markup % Type
    [COLUMN_NAMES.MARKUP_PERCENT_TYPE]: (formFields) => {
      return formFields.markupPercentType || '';
    },

    // 12. Markup ($ or %)
    [COLUMN_NAMES.MARKUP_AMOUNT]: (formFields) => {
      return formFields.markupAmount || '';
    },

    // 13. Notification Email
    [COLUMN_NAMES.NOTIFICATION_EMAIL]: (formFields) => {
      return formFields.notifyEmails
        .map((v: { value: string }) => v.value)
        .join(',');
    },

    // 14. Impact Carrier Exclusions
    [COLUMN_NAMES.IMPACT_CARRIER_EXCLUSIONS]: (formFields) => {
      return formFields.impactScacRestrictions
        ?.map((v) => v?.value)
        .join(',') || '';
    },

    // 15. Impact Origin Postal Exclusions
    [COLUMN_NAMES.IMPACT_ORIGIN_POSTAL_EXCLUSIONS]: (formFields) => {
      return formFields.originZipExclusions || '';
    },

    // 16. Impact Origin State Exclusions
    [COLUMN_NAMES.IMPACT_ORIGIN_STATE_EXCLUSIONS]: (formFields) => {
      return formFields.originStateExclusions
        ?.map((v) => v?.value)
        .join(',') || '';
    },

    // 17. Impact Destination Postal Exclusions
    [COLUMN_NAMES.IMPACT_DESTINATION_POSTAL_EXCLUSIONS]: (formFields) => {
      return formFields.destinationZipExclusions || '';
    },

    // 18. Impact Destination State Exclusions
    [COLUMN_NAMES.IMPACT_DESTINATION_STATE_EXCLUSIONS]: (formFields) => {
      return formFields.destinationStateExclusions
        ?.map((v) => v?.value)
        .join(',') || '';
    },

    // 19. Include Dynamic Quotes
    [COLUMN_NAMES.INCLUDE_DYNAMIC_QUOTES]: (formFields) => {
      return formFields.includeP44Dynamic ? 'Yes' : '';
    },

    // 20. Include Volume Quotes
    [COLUMN_NAMES.INCLUDE_VOLUME_QUOTES]: (formFields) => {
      return formFields.includeP44Volume ? 'Yes' : '';
    },

    // Systems To Rate (not in AnalysisInputConfiguration.cs but may be needed)
    [COLUMN_NAMES.SYSTEMS_TO_RATE]: (formFields) => {
      let value: Systems[] = [SYSTEMS.BG];
      if (formFields.includeP44Dynamic || formFields.includeP44Volume) {
        value.push(SYSTEMS.P44);
      }
      return value.join(',');
    },
  };
